<script setup>
import { ref, computed, watch, nextTick, reactive, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useProductStore } from '@/stores/productsStore';
import { useToast } from 'vue-toastification';
import { useBarcodeLabel } from '@/composables/useBarcodeLabel';
import axios from 'axios';

const authStore = useAuthStore();
const productStore = useProductStore();
const toast = useToast();
const { printProduct, isPrinting } = useBarcodeLabel();

const singleRow = reactive({ price: '' });

const toPrint = reactive({
  sku: '',
  barcode: '',
  price: 0,
  name: '',
});

const sku = ref('');
const skuInput = ref(null);
const rows = ref([
]);

onMounted(() => {
  if (authStore.printLabelPopupVisible) {
    nextTick(() => {
      if (skuInput.value) {
        skuInput.value.focus();
      }
    });
  }
});

const addRow = () => {
  rows.value.push({ qty: '', price: '' });
};

const removeRow = (index) => {
  if (index < 0 || index >= rows.value.length) return;
  rows.value.splice(index, 1);
};

const validateRows = () => {
  if (!sku.value.trim()) {
    toast.error('SKU is required');
    return false;
  }

  // Single-label mode (no rows): validate `singleRow.price`
  if (rows.value.length === 0) {
    const price = parseFloat(singleRow.price);
    if (!price || isNaN(price) || price <= 0) {
      toast.error('Invalid price');
      return false;
    }
    return true;
  }

  // Bulk mode (1+ rows): validate each row's qty/price
  for (let i = 0; i < rows.value.length; i++) {
    const row = rows.value[i];
    if (!row.qty || isNaN(row.qty) || parseInt(row.qty) <= 0) {
      toast.error(`Invalid quantity in row ${i + 1}`);
      return false;
    }
    if (!row.price || isNaN(row.price) || parseFloat(row.price) <= 0) {
      toast.error(`Invalid price in row ${i + 1}`);
      return false;
    }
    // Custom validation as per user query example "222.22 <- fails validation"
    // Assuming price cannot exceed 200 as a placeholder for the validation logic
    if (parseFloat(row.price) > 200) {
      toast.error(`Price exceeds limit in row ${i + 1} (Max 200)`);
      return false;
    }
  }

  return true;
};

const isGenerating = ref(false);

const isGenerateDisabled = computed(() => {
  if (!sku.value || !sku.value.trim()) return true;
  if (rows.value.length === 0) {
    const price = parseFloat(singleRow.price);
    return !price || isNaN(price) || price <= 0;
  }
  for (const row of rows.value) {
    if (!row.qty || isNaN(row.qty) || parseInt(row.qty) <= 0) return true;
    if (!row.price || isNaN(row.price) || parseFloat(row.price) <= 0) return true;
  }
  return false;
});

const closePopup = () => {
  authStore.togglePrintLabelPopup();
};

const clear = () => {
  sku.value = '';
  rows.value = [];
  singleRow.price = '';
  nextTick(() => {
    if (skuInput.value) {
      skuInput.value.focus();
    }
  });
};

const getCachedProduct = (key) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  return cachedProducts[key];
};

const cacheProduct = (key, product) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  cachedProducts[key] = product;
  localStorage.setItem('cachedProducts', JSON.stringify(cachedProducts));
};

const findProductLocally = (query) => {
  const normalized = query.trim().toLowerCase();
  return productStore.products.find((product) => {
    const skuMatch = product?.Sku?.toLowerCase() === normalized;
    const retailMatch = product?.RetailSku?.toLowerCase() === normalized;
    const barcodeMatch = Array.isArray(product?.Barcodes) && product.Barcodes.some((bc) => bc?.toLowerCase() === normalized);
    return skuMatch || retailMatch || barcodeMatch;
  });
};

const mapBackendProduct = (rawProduct) => {
  if (!rawProduct) return null;
  return {
    Barcodes: rawProduct.Barcodes,
    Sku: rawProduct.Sku,
    RetailSku: rawProduct.RetailSku,
    Name: rawProduct.ItemName || rawProduct.Name,
    ImageUrl: rawProduct.ImageUrl,
    MaxQty: rawProduct.Quantity ?? rawProduct.MaxQty,
    Price: rawProduct.Price,
    Discount: rawProduct.DiscountPrice,
    isNonStocking: rawProduct.NonStocking,
    Comment: rawProduct.Comment,
  };
};

const fetchProductFromBackend = async (query) => {
  const token = authStore.managerToken || authStore.token || localStorage.getItem('managerToken') || localStorage.getItem('token');
  const branchId = authStore.branchId || localStorage.getItem('branchId');

  if (!token) {
    toast.error('Authentication token is missing. Please log in again.');
    return null;
  }

  const headers = {
    'store-id': branchId,
    Authorization: `Bearer ${token}`,
  };

  try {
    const response = await axios.get(`/api/Order/GetProductByRetailSkuSingle?retailSku=${query}`, { headers });
    if (response.data?.Status === 200 && response.data?.Product) {
      return mapBackendProduct(response.data.Product);
    }
  } catch (error) {
    console.error('Product lookup error:', error);
    toast.error('Failed to fetch product info');
  }

  return null;
};

const resolveProductForLabel = async () => {
  const query = sku.value?.trim();
  if (!query) {
    toast.error('SKU is required');
    return null;
  }

  const localProduct = findProductLocally(query);
  if (localProduct) return localProduct;

  const cachedProduct = getCachedProduct(query);
  if (cachedProduct) return cachedProduct;

  const backendProduct = await fetchProductFromBackend(query);
  if (backendProduct) {
    // cacheProduct(query, backendProduct);
    return backendProduct;
  }

  return null;
};

const ensureProductBeforeGenerate = async () => {
  const product = await resolveProductForLabel();
  if (!product) {
    toast.error('Item not found locally or on server.');
    return null;
  }

  if (isGenerateDisabled.value) {
    toast.error('Enter a valid SKU and price before generating.');
    return null;
  }

  return product;
};

// Generate label api call handler

const generateLabel = async (product) => {
  const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
  if (!token) {
    console.log('Authentication token is missing. Please log in again.');
    return;
  }
  
  const payload = {
    sku: sku.value,
    price: parseFloat(singleRow.price),
  };
  // if (!validateRows()) return;

  isGenerating.value = true;

  try {
    if (payload) {
      const response = await axios.post('/api/Barcode/generate', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 200) {
        toast.success('Label generated successfully now.');
        isGenerating.value = false;
        toPrint.sku = response.data.sku;
        toPrint.barcode = response.data.barcode;
        toPrint.price = response.data.price;
        const resolvedName = product?.Name || product?.ItemName || response.data?.name || '';
        toPrint.name = resolvedName;
        toPrint.ItemName = resolvedName;
        toPrint.Name = resolvedName;
        await printProduct(toPrint);
        clear();
      } else {
        const apiError = response.data?.error;
        if (apiError) {
          toast.error(apiError);
        } else {
          toast.error(JSON.stringify(response.data || 'Failed to generate label'));
        }
      }
    }
    toast.success('Label generated successfully');
  } catch (error) {
    console.error('Generate label error:', error);
    // If server returned structured error in the response, show it
    const serverData = error?.response?.data;
    const serverMessage = serverData?.error || serverData?.message;
    if (serverMessage) {
      toast.error(serverMessage);
    } else if (serverData) {
      toast.error(JSON.stringify(serverData));
    } else {
      toast.error('Failed to generate label');
    }
  } finally {
    isGenerating.value = false;
  }
};

// Generate bulk label api call handler
const generateBulkLabels = async (product) => {
  const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
  if (!token) {
    console.log('Authentication token is missing. Please log in again.');
    return;
  }
  const bulkPayload = {
    sku: sku.value,
    prices: rows.value.map(row => ({
      count: parseInt(row.qty),
      price: parseFloat(row.price)
    }))
  }
  // if (!validateRows()) return;

  isGenerating.value = true;

  try {
    if (bulkPayload) {
      const response = await axios.post('/api/Barcode/generate-bulk', bulkPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 200) {
        toast.success('Bulk labels generated successfully now.');
        isGenerating.value = false;

        const resp = response.data || {};
        const respSku = resp.sku;
        const respResults = Array.isArray(resp.results) ? resp.results : [];

        // print a label for every barcode returned.
        for (const group of respResults) {
          const groupPrice = group.price;
          const groupBarcodes = Array.isArray(group.barcodes) ? group.barcodes : [];
          for (const bc of groupBarcodes) {
            if (!bc) continue;
            const productForPrint = {
              sku: respSku,
              RetailSku: respSku,
              barcode: bc,
              price: groupPrice,
              Price: groupPrice,
              ItemName: product?.Name || product?.ItemName || '',
              Name: product?.Name || product?.ItemName || '',
            };
            await printProduct(productForPrint);
          }
        }
        clear();
      } else {
        const apiError = response.data?.error;
        if (apiError) {
          toast.error(apiError);
        } else {
          toast.error(JSON.stringify(response.data || 'Failed to generate bulk labels'));
        }
      }
    }
    toast.success('Bulk labels generated successfully');
  } catch (error) {
    console.error('Generate bulk labels error:', error);
    // If server returned structured error in the response, show it
    const serverData = error?.response?.data;
    const serverMessage = serverData?.error || serverData?.message;
    if (serverMessage) {
      toast.error(serverMessage);
    } else if (serverData) {
      toast.error(JSON.stringify(serverData));
    } else {
      toast.error('Failed to generate bulk labels');
    }
  } finally {
    isGenerating.value = false;
  }
};

const handleGenerateLabel = async () => {
  const rawSku = (sku.value || '').trim();
  if (!rawSku.toUpperCase().startsWith('BSUR')) {
    toast.error('SKU must start with BSUR');
    return;
  }

  const product = await ensureProductBeforeGenerate();
  if (!product) return;

  if (rows.value.length >= 1) {
    await generateBulkLabels(product);
  } else {
    await generateLabel(product);
  }
};

const priceValidator = async (e, row) => {
  const el = e?.target;
  const raw = (el?.value ?? row?.price ?? '').toString();
  const caret = typeof el?.selectionStart === 'number' ? el.selectionStart : raw.length;

  let out = '';
  let outCaret = 0;
  let seenDot = false;
  let intDigits = 0;
  let decDigits = 0;

  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    const beforeCaret = i < caret;

    if (c >= '0' && c <= '9') {
      if (!seenDot) {
        if (intDigits < 2) {
          out += c;
          intDigits++;
          if (beforeCaret) outCaret++;
        }
      } else {
        if (decDigits < 2) {
          out += c;
          decDigits++;
          if (beforeCaret) outCaret++;
        }
      }
      continue;
    }

    if (c === '.' && !seenDot) {
      // allow leading dot by turning "." -> "0."
      if (out.length === 0) {
        out += '0';
        intDigits = Math.max(intDigits, 1);
        if (beforeCaret) outCaret++;
      }
      out += '.';
      seenDot = true;
      if (beforeCaret) outCaret++;
    }
  }

  row.price = out;

  // Preserve caret position (works reliably with type="text")
  await nextTick();
  if (el && typeof el.setSelectionRange === 'function') {
    const pos = Math.min(outCaret, row.price.length);
    el.setSelectionRange(pos, pos);
  }
};

</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="authStore.printLabelPopupVisible" 
           @click.self="closePopup"
           class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8 z-[100]">
        <Transition name="modal-inner" class="rounded-2xl">
          <div v-if="authStore.printLabelPopupVisible"
               class="fixed top-20 z-[101] flex items-center justify-center bg-black bg-opacity-50 w-10/12 max-w-2xl">
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full">
              <div class="flex justify-between items-center mb-6">
                <h2 class="font-semibold text-[24px]">Generate Label</h2>
                <button type="button" @click="closePopup" class="text-gray-500 hover:text-gray-700">
                  <i class="pi pi-times text-xl"></i>
                </button>
              </div>

              <div class="mb-6 flex items-end gap-4">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                  <input
                    @keydown="(e) => { if (e.key === '.') e.preventDefault(); }"
                    ref="skuInput"
                    v-model="sku"
                    type="text"
                    placeholder="Enter SKU"
                    class="w-full px-4 py-3 bg-gray-100 border-none rounded-lg outline-none"
                  />
                </div>
                <div v-if="rows.length === 0" class="w-1/3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">PRICE</label>
                  <input
                    v-model="singleRow.price"
                    type="text"
                    inputmode="decimal"
                    autocomplete="off"
                    placeholder="Price"
                    class="w-full px-4 py-3 bg-gray-100 border-none rounded-lg outline-none"
                    @input="priceValidator($event, singleRow)"
                  />
                </div>
              </div>

              <div v-if="rows.length >= 1" class="max-h-[40vh] overflow-y-auto mb-6">
                <div v-for="(row, index) in rows" :key="index" class="flex gap-4 mb-3 items-end">
                  <div class="flex-1">
                    <label v-if="index === 0" class="block text-sm font-medium text-gray-700 mb-1">QTY</label>
                    <input
                      @keydown="(e) => { if (e.key === '.') e.preventDefault(); }"
                      v-model="row.qty"
                      type="number"
                      placeholder="Qty"
                      class="w-full px-4 py-2 bg-gray-100 border-none rounded-lg outline-none"
                      @input="row.qty = row.qty?.toString().slice(0, 2)"
                    />
                  </div>
                  <div class="flex-1">
                    <label v-if="index === 0" class="block text-sm font-medium text-gray-700 mb-1">PRICE</label>
                    <input
                      v-model="row.price"
                      type="text"
                      inputmode="decimal"
                      autocomplete="off"
                      placeholder="Price"
                      class="w-full px-4 py-2 bg-gray-100 border-none rounded-lg outline-none"
                      @input="priceValidator($event, row)"
                    />
                  </div>
                  <button 
                    type="button"
                    @click="removeRow(index)" 
                    class="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <i class="pi pi-trash"></i>
                  </button>
                </div>
                
              </div>
              <button 
                  type="button"
                  @click="addRow" 
                  class="my-2 text-purple-600 font-medium flex items-center gap-2 hover:text-purple-700"
                >
                  <i class="pi pi-plus-circle"></i> Add Row
              </button>
              <div class="flex gap-4">
                <button
                  type="button"
                  @click="clear"
                  class="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Clear
                </button>
                <button
                  type="button"
                  @click="handleGenerateLabel"
                  :disabled="isGenerating || isGenerateDisabled"
                  class="flex-[2] px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
                >
                  <span v-if="!isGenerating">Generate & Print Barcodes</span>
                  <span v-else>Printing...</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

/* Remove arrows from number inputs */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>